import {
  differenceInCalendarDays,
  endOfWeek,
  format,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subWeeks,
  subYears,
} from 'date-fns'
import { capitalize, countBy, filter, update } from 'lodash'

import { TPieChartData } from '~/features/overview/models'
import {
  EReportDashboard,
  REPORT_DASHBOARD_OPTIONS,
  type TCallMetricsOverTimePoint,
  type TCallsByDayOfWeekPoint,
  type TPhoneCallReportingApiResponse,
  type TPhoneCallReportingData,
  type TReportDashboardOption,
} from '~/features/reports/models'
import {
  getDateRangeFromSelection,
  type TReportDateOption,
  type TReportDateRange,
} from '~/features/reports/utils/dateFilter'
import {
  EContactQualification,
  EConversationChannel,
  TConversation,
  TReportContact,
} from '~/models/features'
import { propertyOf } from '~/utils/misc'

export const generatePieChartConversationByChannel = (
  conversations: TConversation[],
): TPieChartData[] => {
  const channelOrder: EConversationChannel[] = [
    EConversationChannel.phone,
    EConversationChannel.email,
    EConversationChannel.text,
    EConversationChannel.chatbot,
  ]
  const countedChannels = countBy(
    conversations.filter((conversation) =>
      Object.values(EConversationChannel).includes(conversation.channel),
    ),
    propertyOf<TConversation>('channel'),
  )
  const channelCount: Record<string, number> = Object.fromEntries(
    Object.entries(countedChannels).map(([channel, count]) => [channel, count]),
  )
  // Sort the keys based on channelOrder
  const sortedKeys = Object.keys(channelCount).sort((a, b) => {
    return (
      channelOrder.indexOf(a as EConversationChannel) -
      channelOrder.indexOf(b as EConversationChannel)
    )
  })
  return sortedKeys.map((channel) => ({
    id: channel,
    value: channelCount[channel],
    label: capitalize(channel),
  }))
}

type TWeekDataConversation = {
  [channel in EConversationChannel]: number
} & { week: string }

export function generateConverstionsByChannelAndDate(
  data: TConversation[],
): TWeekDataConversation[] {
  const weeks: Record<string, TWeekDataConversation> = {}

  // Group data by week and channel
  data.forEach((item) => {
    const updatedDate = new Date(item.updatedDate)
    // Calculate the date of the Sunday of the current week
    const weekStart = new Date(updatedDate)
    weekStart.setDate(weekStart.getDate() - updatedDate.getDay())
    // Format the week as "MM/DD"
    const week = `${weekStart.getMonth() + 1}/${weekStart.getDate()}`
    if (!weeks[week]) {
      weeks[week] = { week } as TWeekDataConversation
      Object.values(EConversationChannel).forEach((channel) => {
        weeks[week][channel] = 0
      })
    }
    update(weeks[week], item.channel, (count) => (count || 0) + 1)
  })

  return Object.values(weeks)
}

type TWeekDataContacts = {
  [qualification in EContactQualification]: number
} & { week: string }

export function generateContactsByWeekAndQualification(
  contacts: TReportContact[],
): TWeekDataContacts[] {
  const weeks: Record<string, TWeekDataContacts> = {}

  // Group data by week and qualification
  contacts.forEach((contact) => {
    const updatedDate = new Date(contact.mostRecentConversationDate)
    const weekStart = new Date(updatedDate)
    weekStart.setDate(weekStart.getDate() - updatedDate.getDay())
    const week = `${weekStart.getMonth() + 1}/${weekStart.getDate()}`

    if (!weeks[week]) {
      weeks[week] = { week } as TWeekDataContacts
      Object.values(EContactQualification).forEach((qualification) => {
        weeks[week][qualification] = 0
      })
    }
    const qualification = contact.qualification || 'undetermined'
    if (qualification !== EContactQualification.qualified) {
      update(
        weeks[week],
        EContactQualification.disqualified,
        (count) => (count || 0) + 1,
      )
    } else {
      update(
        weeks[week],
        EContactQualification.qualified,
        (count) => (count || 0) + 1,
      )
    }
  })

  return Object.values(weeks)
}

export const generateArrayOfDates = (
  numberOfDays: number,
  endDateStr?: string,
): string[] => {
  const endDate = endDateStr ? new Date(`${endDateStr}T00:00:00`) : new Date()
  endDate.setHours(0, 0, 0, 0)

  const dateArray: string[] = []

  for (let i = numberOfDays - 1; i >= 0; i -= 1) {
    const currentDate = new Date(endDate)
    currentDate.setDate(endDate.getDate() - i)

    const month = (currentDate.getMonth() + 1).toString().padStart(2, '0')
    const day = currentDate.getDate().toString().padStart(2, '0')
    dateArray.push(`${month}/${day}`)
  }

  return dateArray
}

export const generateConversationsPerDayArray = (
  conversations: TConversation[],
  selectedPeriod: number,
  shouldFilterOutEmptyDates: boolean,
  endDate?: string,
): { datesArray: string[]; numberOfConversationsArray: number[] } => {
  const dateArray = generateArrayOfDates(selectedPeriod, endDate)
  const conversationsByDate: Record<string, number> = {}

  dateArray.forEach((date) => {
    conversationsByDate[date] = 0
  })

  conversations.forEach((conversation: TConversation) => {
    const conversationDate = new Date(conversation.updatedDate)
    const month = (conversationDate.getMonth() + 1).toString().padStart(2, '0')
    const day = conversationDate.getDate().toString().padStart(2, '0')
    const formattedDate = `${month}/${day}`

    if (conversationsByDate[formattedDate] !== undefined) {
      conversationsByDate[formattedDate] += 1
    }
  })

  if (shouldFilterOutEmptyDates) {
    let index = 0
    Object.keys(conversationsByDate).forEach((key) => {
      if (conversationsByDate[key] === 0 && index !== 0) {
        delete conversationsByDate[key]
      }
      index += 1
    })
  }
  const datesArray = Object.keys(conversationsByDate)
  const numberOfConversationsArray = Object.values(conversationsByDate)
  return { datesArray, numberOfConversationsArray }
}

export const getNumberOfConversationWithContactInfo = (
  conversations: TConversation[],
) => {
  const conversationsWithContact = filter(
    conversations,
    (conversation) => conversation.hasContactInfo,
  )

  return conversationsWithContact.length
}

export const EPhoneCallDatePreset = {
  defaults: 'defaults',
  thisWeek: 'this-week',
  lastWeek: 'last-week',
  lastMonth: 'last-month',
  last3Months: 'last-3-months',
  lastYear: 'last-year',
  allTime: 'all-time',
  custom: 'custom',
  last7: 'last-7',
  last30: 'last-30',
  monthToDate: 'month-to-date',
  today: 'today',
  yesterday: 'yesterday',
} as const

export const DEFAULT_PHONE_REPORT_DATE_PRESET = EPhoneCallDatePreset.last30

export function getPhoneCallReportDateOptions(): TReportDateOption[] {
  const top: TReportDateOption[] = [
    {
      key: EPhoneCallDatePreset.defaults,
      label: 'Defaults',
      value: EPhoneCallDatePreset.defaults,
    },
    {
      key: EPhoneCallDatePreset.today,
      label: 'Today',
      value: EPhoneCallDatePreset.today,
    },
    {
      key: EPhoneCallDatePreset.thisWeek,
      label: 'This week',
      value: EPhoneCallDatePreset.thisWeek,
    },
    {
      key: EPhoneCallDatePreset.lastWeek,
      label: 'Last week',
      value: EPhoneCallDatePreset.lastWeek,
    },
    {
      key: EPhoneCallDatePreset.monthToDate,
      label: 'Month to date',
      value: EPhoneCallDatePreset.monthToDate,
    },
    {
      key: EPhoneCallDatePreset.lastMonth,
      label: 'Last month',
      value: EPhoneCallDatePreset.lastMonth,
    },
    {
      key: EPhoneCallDatePreset.last7,
      label: 'Last 7 days',
      value: EPhoneCallDatePreset.last7,
    },
    {
      key: EPhoneCallDatePreset.last30,
      label: 'Last 30 days',
      value: EPhoneCallDatePreset.last30,
    },
    {
      key: EPhoneCallDatePreset.last3Months,
      label: 'Last 3 months',
      value: EPhoneCallDatePreset.last3Months,
    },
    {
      key: EPhoneCallDatePreset.lastYear,
      label: 'Last year',
      value: EPhoneCallDatePreset.lastYear,
    },
    {
      key: EPhoneCallDatePreset.allTime,
      label: 'All time',
      value: EPhoneCallDatePreset.allTime,
    },
    {
      key: EPhoneCallDatePreset.custom,
      label: 'Custom',
      value: EPhoneCallDatePreset.custom,
    },
  ]

  return top
}

function formatDateString(date: Date): string {
  return format(date, 'yyyy-MM-dd')
}

export function getPhoneCallReportDateRange(
  preset: string,
  customRange: { startDate: string; endDate: string } | null,
): TReportDateRange {
  const now = new Date()
  if (preset === EPhoneCallDatePreset.today) {
    const d = formatDateString(now)
    return { startDate: d, endDate: d, periodDays: 1 }
  }

  if (preset === EPhoneCallDatePreset.thisWeek) {
    const start = startOfWeek(now, { weekStartsOn: 1 })
    return {
      startDate: formatDateString(start),
      endDate: formatDateString(now),
      periodDays: differenceInCalendarDays(now, start) + 1,
    }
  }

  if (preset === EPhoneCallDatePreset.lastWeek) {
    const reference = subWeeks(now, 1)
    const start = startOfWeek(reference, { weekStartsOn: 1 })
    const end = endOfWeek(reference, { weekStartsOn: 1 })
    return {
      startDate: formatDateString(start),
      endDate: formatDateString(end),
      periodDays: differenceInCalendarDays(end, start) + 1,
    }
  }

  if (preset === EPhoneCallDatePreset.monthToDate) {
    const start = startOfMonth(now)
    return {
      startDate: formatDateString(start),
      endDate: formatDateString(now),
      periodDays: differenceInCalendarDays(now, start) + 1,
    }
  }

  if (preset === EPhoneCallDatePreset.lastMonth) {
    const previousMonth = subMonths(now, 1)
    const start = startOfMonth(previousMonth)
    const end = new Date(startOfMonth(now).getTime() - 1)
    return {
      startDate: formatDateString(start),
      endDate: formatDateString(end),
      periodDays: differenceInCalendarDays(end, start) + 1,
    }
  }

  if (preset === EPhoneCallDatePreset.last3Months) {
    const start = subMonths(now, 3)
    return {
      startDate: formatDateString(start),
      endDate: formatDateString(now),
      periodDays: differenceInCalendarDays(now, start) + 1,
    }
  }

  if (preset === EPhoneCallDatePreset.lastYear) {
    const start = subYears(now, 1)
    return {
      startDate: formatDateString(start),
      endDate: formatDateString(now),
      periodDays: differenceInCalendarDays(now, start) + 1,
    }
  }

  if (preset === EPhoneCallDatePreset.allTime) {
    const start = startOfYear(new Date(2000, 0, 1))
    return {
      startDate: formatDateString(start),
      endDate: formatDateString(now),
      periodDays: differenceInCalendarDays(now, start) + 1,
    }
  }

  if (
    preset === EPhoneCallDatePreset.custom &&
    customRange?.startDate &&
    customRange.endDate
  ) {
    const s = new Date(`${customRange.startDate}T00:00:00`)
    const e = new Date(`${customRange.endDate}T00:00:00`)
    if (s <= e) {
      return {
        startDate: customRange.startDate,
        endDate: customRange.endDate,
        periodDays: differenceInCalendarDays(e, s) + 1,
      }
    }
  }

  if (preset === EPhoneCallDatePreset.custom) {
    return getDateRangeFromSelection('last-7')
  }

  if (preset === EPhoneCallDatePreset.defaults) {
    return getDateRangeFromSelection('last-30')
  }

  if (
    preset === EPhoneCallDatePreset.last7 ||
    preset === EPhoneCallDatePreset.last30 ||
    /^\d{4}-\d{2}$/.test(preset)
  ) {
    return getDateRangeFromSelection(preset)
  }

  return getDateRangeFromSelection('last-30')
}

const MAX_PHONE_REPORT_RANGE_DAYS = 31

/**
 * The async export runs in the background (no gateway timeout) and streams its sections, so it
 * takes far longer custom ranges than the on-screen reports.
 */
export const MAX_EXPORT_RANGE_DAYS = 731

export function getPhoneCallCustomRangeError(
  startDate: string,
  endDate: string,
  maxDays = MAX_PHONE_REPORT_RANGE_DAYS,
): string | null {
  if (!startDate || !endDate) {
    return 'Start and end dates are required'
  }
  const s = new Date(`${startDate}T00:00:00.000Z`)
  const e = new Date(`${endDate}T23:59:59.999Z`)
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) {
    return 'Invalid date'
  }
  if (s >= e) {
    return 'End must be after start'
  }
  const inclusiveDays =
    differenceInCalendarDays(
      new Date(`${endDate}T00:00:00.000Z`),
      new Date(`${startDate}T00:00:00.000Z`),
    ) + 1
  if (inclusiveDays > maxDays) {
    return `Range cannot exceed ${maxDays} days`
  }
  return null
}

export function getDefaultCustomDateRange(): {
  startDate: string
  endDate: string
} {
  const end = new Date()
  const start = subDays(end, 6)
  return {
    startDate: formatDateString(start),
    endDate: formatDateString(end),
  }
}

const REPORT_NUMBER_FORMATTER = new Intl.NumberFormat('en-US')

export function formatNumberWithCommas(value: number): string {
  return REPORT_NUMBER_FORMATTER.format(value)
}

export function formatLeadDate(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return '—'
  }
  return format(date, 'MMM d')
}

export function formatOptionalDate(value: string | null): string {
  if (!value) {
    return '—'
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return '—'
  }
  return format(date, 'MMM d')
}

/** Elapsed time in hours and minutes (e.g. 3h 29min, 5min). */
export function formatDurationHoursMinutes(
  totalMinutes: number | null | undefined,
): string {
  if (totalMinutes == null || totalMinutes < 0) {
    return '—'
  }
  if (totalMinutes === 0) {
    return '<1 min'
  }
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours === 0) {
    return `${minutes}min`
  }
  return `${hours}h ${minutes}min`
}

export function bucketLabel(outboundCalls: number): string {
  if (outboundCalls === 1) {
    return '1 call'
  }
  return `${outboundCalls} calls`
}

export type TVoipMatchbackBucketRow = {
  outboundCalls: number
  leadCount: number
  label: string
}

/** Table rows for the bucket chart: 0–2 calls, then 3+ total, then each 3+ count. */
export function buildVoipMatchbackBucketRows(
  buckets: { outboundCalls: number; leadCount: number }[],
): TVoipMatchbackBucketRow[] {
  const sorted = [...buckets].sort((a, b) => a.outboundCalls - b.outboundCalls)
  const underThree = sorted.filter((b) => b.outboundCalls < 3)
  const threePlus = sorted.filter((b) => b.outboundCalls >= 3)
  const threePlusTotal = threePlus.reduce((sum, b) => sum + b.leadCount, 0)

  return [
    ...underThree.map((b) => ({
      ...b,
      label: bucketLabel(b.outboundCalls),
    })),
    ...(threePlusTotal > 0
      ? [{ outboundCalls: 3, leadCount: threePlusTotal, label: '3+ calls' }]
      : []),
    ...threePlus.map((b) => ({
      ...b,
      label: `${b.outboundCalls}x calls`,
    })),
  ]
}

/**
 * Default report shape; used for fallbacks and for coalescing nulls from the API.
 */
export const PHONE_CALL_REPORTING_INITIAL: TPhoneCallReportingData = {
  hasInboundDirectCalls: false,
  totalCalls: { count: 0, previousCount: null, changeFromLastPeriod: 0 },
  voicemails: { count: 0, previousCount: null, changeFromLastPeriod: 0 },
  missedCalls: {
    count: 0,
    previousCount: null,
    percentage: null,
    changeFromLastPeriod: 0,
  },
  answeredRate: { rate: null, previousRate: null, changeFromLastPeriod: 0 },
  totalInbound: { count: 0, previousCount: null, changeFromLastPeriod: 0 },
  inboundBreakdown: { aiAgent: 0, direct: 0 },
  totalOutbound: { count: 0, previousCount: null, changeFromLastPeriod: 0 },
  outboundBreakdown: { aiAgent: 0, direct: 0 },
  callsByLocation: [],
  callsByLocationTruncated: false,
  callsByAssistant: [],
  callsByAssistantTruncated: false,
  callVolumeOverTime: [],
  callMetricsOverTime: [],
  callsByHourOfDay: [],
  callsByDayOfWeek: [],
  answeredByHourAndDayOfWeek: [],
  avgCallDurationSeconds: 0,
}

function coalesceCountWithChange(
  api: TPhoneCallReportingData['totalCalls'] | null | undefined,
  initial: TPhoneCallReportingData['totalCalls'],
) {
  return {
    count: api?.count ?? initial.count,
    previousCount: api?.previousCount ?? initial.previousCount,
    changeFromLastPeriod:
      api?.changeFromLastPeriod ?? initial.changeFromLastPeriod,
  }
}

function coalesceMissed(
  api: TPhoneCallReportingData['missedCalls'] | null | undefined,
  initial: TPhoneCallReportingData['missedCalls'],
) {
  return {
    count: api?.count ?? initial.count,
    previousCount: api?.previousCount ?? initial.previousCount,
    // Preserve null — do not coerce to 0 (null means "not applicable").
    percentage:
      api?.percentage === undefined ? initial.percentage : api.percentage,
    changeFromLastPeriod:
      api?.changeFromLastPeriod ?? initial.changeFromLastPeriod,
  }
}

function coalesceAnswered(
  api: TPhoneCallReportingData['answeredRate'] | null | undefined,
  initial: TPhoneCallReportingData['answeredRate'],
) {
  return {
    // Preserve null — do not coerce to 0 (null means "not applicable").
    rate: api?.rate === undefined ? initial.rate : api.rate,
    previousRate:
      api?.previousRate === undefined ? initial.previousRate : api.previousRate,
    changeFromLastPeriod:
      api?.changeFromLastPeriod ?? initial.changeFromLastPeriod,
  }
}

function coalesceBreakdown(
  api: TPhoneCallReportingData['inboundBreakdown'] | null | undefined,
  initial: TPhoneCallReportingData['inboundBreakdown'],
) {
  return {
    aiAgent: api?.aiAgent ?? initial.aiAgent,
    direct: api?.direct ?? initial.direct,
  }
}

/**
 * The response type claims every field is present, but real payloads omit them
 * (older backends, days with no direct calls), so treat each as possibly
 * missing on the way in.
 */
type TCallMetricsOverTimeRowInput = Omit<
  Partial<TCallMetricsOverTimePoint>,
  'date'
> & {
  date: string
}

const isFiniteNumber = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value)

const toCount = (value: number | null | undefined): number =>
  isFiniteNumber(value) ? value : 0

/**
 * A rate stays null when the day had no inbound direct calls. Coercing a
 * missing rate to 0 would draw a real-looking dip in the trend charts, so
 * anything non-numeric is normalized to null and dropped from the line.
 */
const toRate = (value: number | null | undefined): number | null =>
  isFiniteNumber(value) ? value : null

/**
 * `inboundDirectCount` is a new field the API may not send yet (an older
 * backend, or a frontend deploy that lands before the backend's), so treat
 * it as possibly missing on the way in.
 */
type TCallsByDayOfWeekRowInput = Omit<
  Partial<TCallsByDayOfWeekPoint>,
  'dayOfWeek'
> & {
  dayOfWeek: number
}

/**
 * Older/mid-rollout API payloads may omit `inboundDirectCount`; fall back to
 * `answeredCalls + missedCalls` (the previous denominator) rather than 0, so
 * the answered-rate column still shows a rate instead of blanking every day
 * to "—" until the backend ships.
 */
function normalizeCallsByDayOfWeekRow(
  row: TCallsByDayOfWeekRowInput,
): TCallsByDayOfWeekPoint {
  const answeredCalls = toCount(row.answeredCalls)
  const missedCalls = toCount(row.missedCalls)
  return {
    dayOfWeek: row.dayOfWeek,
    totalCalls: toCount(row.totalCalls),
    inboundDirectCount: isFiniteNumber(row.inboundDirectCount)
      ? row.inboundDirectCount
      : answeredCalls + missedCalls,
    answeredCalls,
    missedCalls,
  }
}

/** Older API payloads omit daily `totalCalls`; derive from inbound + outbound. */
function normalizeCallMetricsOverTimeRow(
  row: TCallMetricsOverTimeRowInput,
): TCallMetricsOverTimePoint {
  const inbound = toCount(row.inbound)
  const outbound = toCount(row.outbound)

  return {
    date: row.date,
    totalCalls: isFiniteNumber(row.totalCalls)
      ? row.totalCalls
      : inbound + outbound,
    answeredCount: toCount(row.answeredCount),
    answeredRate: toRate(row.answeredRate),
    missedCount: toCount(row.missedCount),
    missedPercentage: toRate(row.missedPercentage),
    voicemailCount: toCount(row.voicemailCount),
    inbound,
    outbound,
    aiAgent: toCount(row.aiAgent),
    direct: toCount(row.direct),
  }
}

/**
 * Run once when the report API payload is received. Coalesces whole missing groups
 * and null fields inside groups (e.g. `{ count: null }`) so the UI can render safely.
 */
export function normalizePhoneCallReportResponse(
  data: TPhoneCallReportingApiResponse | null | undefined,
): TPhoneCallReportingData {
  if (!data) {
    return PHONE_CALL_REPORTING_INITIAL
  }

  return {
    hasInboundDirectCalls: data.hasInboundDirectCalls ?? false,
    totalCalls: coalesceCountWithChange(
      data.totalCalls,
      PHONE_CALL_REPORTING_INITIAL.totalCalls,
    ),
    voicemails: coalesceCountWithChange(
      data.voicemails,
      PHONE_CALL_REPORTING_INITIAL.voicemails,
    ),
    missedCalls: coalesceMissed(
      data.missedCalls,
      PHONE_CALL_REPORTING_INITIAL.missedCalls,
    ),
    answeredRate: coalesceAnswered(
      data.answeredRate,
      PHONE_CALL_REPORTING_INITIAL.answeredRate,
    ),
    totalInbound: coalesceCountWithChange(
      data.totalInbound,
      PHONE_CALL_REPORTING_INITIAL.totalInbound,
    ),
    inboundBreakdown: coalesceBreakdown(
      data.inboundBreakdown,
      PHONE_CALL_REPORTING_INITIAL.inboundBreakdown,
    ),
    totalOutbound: coalesceCountWithChange(
      data.totalOutbound,
      PHONE_CALL_REPORTING_INITIAL.totalOutbound,
    ),
    outboundBreakdown: coalesceBreakdown(
      data.outboundBreakdown,
      PHONE_CALL_REPORTING_INITIAL.outboundBreakdown,
    ),
    callsByLocationTruncated: data.callsByLocationTruncated ?? false,
    callsByAssistantTruncated: data.callsByAssistantTruncated ?? false,
    callsByLocation: (
      data.callsByLocation ?? PHONE_CALL_REPORTING_INITIAL.callsByLocation
    ).map((row) => ({
      locationId: row.locationId ?? '',
      name: row.name ?? '—',
      city: row.city ?? null,
      state: row.state ?? null,
      totalCalls: row.totalCalls ?? 0,
      inboundCalls: row.inboundCalls ?? 0,
      inboundAiCalls: row.inboundAiCalls ?? 0,
      inboundDirectCalls: row.inboundDirectCalls ?? 0,
      outboundCalls: row.outboundCalls ?? 0,
      outboundAiCalls: row.outboundAiCalls ?? 0,
      outboundDirectCalls: row.outboundDirectCalls ?? 0,
      aiCalls: row.aiCalls ?? 0,
      directCalls: row.directCalls ?? 0,
      missedCalls: row.missedCalls ?? 0,
      voicemailCalls: row.voicemailCalls ?? 0,
      avgCallDurationSeconds: row.avgCallDurationSeconds ?? 0,
      answeredCalls: row.answeredCalls ?? 0,
      answeredRate:
        row.answeredRate === undefined || row.answeredRate === null
          ? null
          : row.answeredRate,
    })),
    callsByAssistant: (
      data.callsByAssistant ?? PHONE_CALL_REPORTING_INITIAL.callsByAssistant
    ).map((row) => ({
      agentId: row.agentId ?? '',
      name: row.name ?? '—',
      phoneNumber: row.phoneNumber ?? null,
      locationId: row.locationId ?? null,
      totalCalls: row.totalCalls ?? 0,
      inboundCalls: row.inboundCalls ?? 0,
      inboundAiCalls: row.inboundAiCalls ?? 0,
      inboundDirectCalls: row.inboundDirectCalls ?? 0,
      outboundCalls: row.outboundCalls ?? 0,
      outboundAiCalls: row.outboundAiCalls ?? 0,
      outboundDirectCalls: row.outboundDirectCalls ?? 0,
      aiCalls: row.aiCalls ?? 0,
      directCalls: row.directCalls ?? 0,
      missedCalls: row.missedCalls ?? 0,
      voicemailCalls: row.voicemailCalls ?? 0,
      avgCallDurationSeconds: row.avgCallDurationSeconds ?? 0,
      answeredCalls: row.answeredCalls ?? 0,
      answeredRate:
        row.answeredRate === undefined || row.answeredRate === null
          ? null
          : row.answeredRate,
    })),
    callVolumeOverTime:
      data.callVolumeOverTime ??
      PHONE_CALL_REPORTING_INITIAL.callVolumeOverTime,
    callMetricsOverTime: (
      data.callMetricsOverTime ??
      PHONE_CALL_REPORTING_INITIAL.callMetricsOverTime
    ).map(normalizeCallMetricsOverTimeRow),
    callsByHourOfDay:
      data.callsByHourOfDay ?? PHONE_CALL_REPORTING_INITIAL.callsByHourOfDay,
    callsByDayOfWeek: (
      data.callsByDayOfWeek ?? PHONE_CALL_REPORTING_INITIAL.callsByDayOfWeek
    ).map(normalizeCallsByDayOfWeekRow),
    answeredByHourAndDayOfWeek:
      data.answeredByHourAndDayOfWeek ??
      PHONE_CALL_REPORTING_INITIAL.answeredByHourAndDayOfWeek,
    avgCallDurationSeconds:
      data.avgCallDurationSeconds ??
      PHONE_CALL_REPORTING_INITIAL.avgCallDurationSeconds,
  }
}

const REPORTS_BASE_PATH = '/reports'

/** AI Assistant, Phone Call, and Export Data are available without voip; VoIP dashboards require it. */
export function getReportDashboardOptions(
  hasVoipOrgFeature: boolean,
): TReportDashboardOption[] {
  return REPORT_DASHBOARD_OPTIONS.filter(
    (option) =>
      option.key === EReportDashboard.aiAssistant ||
      option.key === EReportDashboard.phoneCalls ||
      option.key === EReportDashboard.download ||
      hasVoipOrgFeature,
  )
}

export const isReportDashboardSlug = (
  value: string,
): value is EReportDashboard =>
  (Object.values(EReportDashboard) as string[]).includes(value)

export const parseReportDashboardSlug = (
  slug: string | undefined,
): EReportDashboard => {
  if (!slug) return EReportDashboard.aiAssistant
  if (isReportDashboardSlug(slug)) return slug
  return EReportDashboard.aiAssistant
}

export const getReportsPathForDashboard = (
  dashboard: EReportDashboard,
): string => {
  if (dashboard === EReportDashboard.aiAssistant) return REPORTS_BASE_PATH
  return `${REPORTS_BASE_PATH}/${dashboard}`
}
